import { useState, useMemo, useCallback, useEffect } from "react";
import { useCountries, useDeleteCountry } from "@/hooks/useCountries";
import { CountryDialog } from "@/components/countries/CountryDialog";
import { CountriesTable } from "@/components/countries/CountriesTable";
import { CountriesFilters } from "@/components/countries/CountriesFilters";
import { SmartButton } from "@/components/ui/interactive/SmartButton";
import { RealTimeUpdater } from "@/components/ui/interactive/RealTimeUpdater";
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { DataExport } from "@/components/ui/data-export";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { DynamicCard } from "@/components/ui/interactive/DynamicCard";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, RefreshCw, Edit, Trash2, Flag, Globe } from "lucide-react";
const Countries = () => {
  const {
    data: countries = [],
    isLoading,
    refetch
  } = useCountries();
  const deleteCountry = useDeleteCountry();
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);

  // Real-time notifications
  useEffect(() => {
    const channel = supabase.channel('countries_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'countries'
    }, payload => {
      console.log('Country change detected:', payload);
      refetch();
      switch (payload.eventType) {
        case 'INSERT':
          toast.success('تم إضافة دولة جديدة');
          break;
        case 'UPDATE':
          toast.info('تم تحديث بيانات دولة');
          break;
        case 'DELETE':
          toast.warning('تم حذف دولة');
          break;
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Advanced search config
  const searchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الدول...",
    filters: []
  }), []);
  const filteredCountries = useMemo(() => {
    return countries.filter(country => {
      const matchesSearch = country.name_ar.toLowerCase().includes(searchTerm.toLowerCase()) || country.name_en && country.name_en.toLowerCase().includes(searchTerm.toLowerCase()) || country.default_currency && country.default_currency.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [countries, searchTerm]);

  // Table columns
  const tableColumns: TableColumn<any>[] = useMemo(() => [{
    key: 'name_ar',
    title: 'اسم الدولة',
    sortable: true,
    render: (_, record) => <div className="font-medium text-card-foreground">{record.name_ar}</div>
  }, {
    key: 'name_en',
    title: 'الاسم بالإنجليزية',
    sortable: true,
    render: (_, record) => record.name_en || '-'
  }, {
    key: 'default_currency',
    title: 'العملة الافتراضية',
    render: (_, record) => record.default_currency ? <Badge variant="outline">{record.default_currency}</Badge> : '-'
  }, {
    key: 'actions',
    title: 'الإجراءات',
    align: 'center',
    render: (_, record) => <div className="flex gap-2 justify-center">
          <SmartButton variant="outline" size="sm" onClick={() => handleEdit(record)}>
            تعديل
          </SmartButton>
          <SmartButton variant="destructive" size="sm" onClick={() => handleDelete(record.id)} requireConfirmation confirmationTitle="حذف الدولة" confirmationDescription={`هل أنت متأكد من حذف دولة ${record.name_ar}؟`} successMessage="تم حذف الدولة" showSuccessState>
            حذف
          </SmartButton>
        </div>
  }], []);
  const handleEdit = useCallback((country: any) => {
    setSelectedCountry(country);
    setDialogOpen(true);
  }, []);
  const handleDelete = useCallback(async (countryId: string) => {
    try {
      await deleteCountry.mutateAsync(countryId);
      toast.success('تم حذف الدولة بنجاح');
    } catch (error) {
      toast.error('حدث خطأ أثناء حذف الدولة');
    }
  }, []);
  const handleSearch = useCallback((query: string) => {
    setSearchTerm(query);
  }, []);
  const handleManualRefresh = useCallback(async () => {
    toast.info("جاري تحديث البيانات...");
    try {
      await refetch();
      toast.success("تم تحديث البيانات بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء تحديث البيانات");
    }
  }, [refetch]);
  if (isLoading) {
    return <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل الدول..." />
      </div>;
  }
  return <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <RealTimeUpdater tableName="countries" onUpdate={() => refetch()} showStatus={true}>
        {/* الهيدر */}
        <div className="flex items-center gap-2 mb-6">
          <h1 className="text-2xl font-bold text-card-foreground">إدارة الدول</h1>
          
          <ViewToggle view={view} onViewChange={setView} />
          
          <DataExport data={filteredCountries} columns={[{
            key: 'name_ar',
            title: 'الاسم بالعربية',
            dataIndex: 'name_ar'
          }, {
            key: 'name_en',
            title: 'الاسم بالإنجليزية',
            dataIndex: 'name_en'
          }, {
            key: 'code',
            title: 'الكود',
            dataIndex: 'code'
          }]} filename="الدول" />
          
          <SmartButton onClick={() => {
            setSelectedCountry(null);
            setDialogOpen(true);
          }} variant="default" size="default" className="gap-2">
            <Plus className="h-4 w-4" />
            إضافة دولة
          </SmartButton>
        </div>

        {/* البحث المتقدم */}
        <AdvancedSearch config={searchConfig} onSearch={query => handleSearch(query)} initialQuery={searchTerm} />

        {/* المحتوى */}
        {view === 'cards' ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCountries.map(country => <DynamicCard key={country.id} title={country.name_ar} description={country.name_en} badge={country.default_currency ? {
          text: country.default_currency,
          variant: "outline"
        } : undefined} hoverEffect="lift" pulseOnUpdate onEdit={() => handleEdit(country)} onDelete={() => handleDelete(country.id)}>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Flag className="h-4 w-4" />
                  <span>{country.default_currency || 'عملة غير محددة'}</span>
                </div>
              </DynamicCard>)}
          </div> : <OptimizedTable columns={tableColumns} data={filteredCountries} loading={isLoading} pagination={{
        current: 1,
        pageSize: 20,
        total: filteredCountries.length,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100]
      }} rowKey="id" emptyText="لا توجد دول" size="middle" />}
        
        {filteredCountries.length === 0 && !isLoading && <div className="text-center py-12 text-muted-foreground">
            <p>لم يتم العثور على أي دول.</p>
          </div>}

        <CountryDialog country={selectedCountry} trigger={null} onSuccess={() => {
        refetch();
        setDialogOpen(false);
        setSelectedCountry(null);
      }} open={dialogOpen} onOpenChange={setDialogOpen} />
      </RealTimeUpdater>
    </div>;
};
export default Countries;